import Swal from "sweetalert2";
import { ensureThemeJsonArray } from "./themeAssets";
import { parseWordPuzzleFromTheme } from "../pages/game/wordSearchUtils";
import { parseUnscrambleFromTheme } from "../pages/game/unscrambleUtils";

/** Keep in sync with admin themeupdate minimums. */
const WP_MIN_WORDS = 2;
const UNSCRAMBLE_MIN_PAIRS = 2;
const QUIZ_MIN_QUESTIONS = 3;
const IMAGE_QUIZ_MIN_QUESTIONS = 3;
const IMAGE_ONLY_MIN_QUESTIONS = 3;

function hasText(val) {
  const s = String(val ?? "").trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  return lower !== "null" && lower !== "undefined" && lower !== "none";
}

function optionCount(row) {
  return ["option_one", "option_two", "option_three", "option_four"].filter(
    (k) => hasText(row?.[k])
  ).length;
}

function getQuizAudioFile(row) {
  return row?.audio_file ?? row?.audio ?? row?.question_audio ?? "";
}

function getImageFile(row) {
  return row?.image_file ?? row?.image ?? row?.question_image ?? "";
}

function quizRowHasContent(row) {
  return (
    hasText(getQuizAudioFile(row)) ||
    hasText(row?.question_name ?? row?.question) ||
    optionCount(row) > 0 ||
    hasText(row?.correct_answer)
  );
}

function imageRowHasContent(row) {
  return (
    hasText(getImageFile(row)) ||
    optionCount(row) > 0 ||
    hasText(row?.correct_answer)
  );
}

function isQuizRowComplete(row) {
  return (
    hasText(getQuizAudioFile(row)) &&
    optionCount(row) >= 4 &&
    hasText(row?.correct_answer)
  );
}

function isImageRowComplete(row) {
  return (
    hasText(getImageFile(row)) &&
    optionCount(row) >= 4 &&
    hasText(row?.correct_answer)
  );
}

function collectWordPuzzleMissing(themeData, missing) {
  const words = parseWordPuzzleFromTheme(themeData);
  if (words.length < WP_MIN_WORDS) {
    missing.push(
      `Stage 1 (Word Puzzle): need at least ${WP_MIN_WORDS} words (found ${words.length})`
    );
  }
}

function collectUnscrambleMissing(themeData, missing) {
  const pairs = parseUnscrambleFromTheme(themeData);
  if (pairs.length < UNSCRAMBLE_MIN_PAIRS) {
    missing.push(
      `Stage 2 (Unscramble): need at least ${UNSCRAMBLE_MIN_PAIRS} complete question/answer pairs (found ${pairs.length})`
    );
  }
}

/**
 * Every question in the list must be fully complete.
 * Having the minimum number of complete questions is NOT enough if any extra row is incomplete.
 */
function collectMediaStageMissing({
  rows,
  stageLabel,
  minQuestions,
  isComplete,
  mediaLabel,
  getMedia,
  missing,
}) {
  if (rows.length < minQuestions) {
    missing.push(
      `${stageLabel}: need at least ${minQuestions} questions (found ${rows.length})`
    );
  }

  rows.forEach((row, i) => {
    const n = i + 1;
    const problems = [];

    if (!hasText(getMedia(row))) {
      problems.push(`${mediaLabel} not uploaded`);
    }
    if (optionCount(row) < 4) {
      problems.push("missing one or more options");
    }
    if (!hasText(row?.correct_answer)) {
      problems.push("missing correct answer");
    }

    if (problems.length || !isComplete(row)) {
      missing.push(
        `${stageLabel} question ${n}: ${problems.join("; ") || "incomplete data"}`
      );
    }
  });
}

function collectQuizMissing(themeData, missing) {
  const rows = ensureThemeJsonArray(themeData?.quiz).filter(quizRowHasContent);
  collectMediaStageMissing({
    rows,
    stageLabel: "Stage 3 (Quiz)",
    minQuestions: QUIZ_MIN_QUESTIONS,
    isComplete: isQuizRowComplete,
    mediaLabel: "audio file",
    getMedia: getQuizAudioFile,
    missing,
  });
}

function collectImageQuizMissing(themeData, missing) {
  const rows = ensureThemeJsonArray(themeData?.image_quiz).filter(imageRowHasContent);
  collectMediaStageMissing({
    rows,
    stageLabel: "Stage 4 (Image Quiz)",
    minQuestions: IMAGE_QUIZ_MIN_QUESTIONS,
    isComplete: isImageRowComplete,
    mediaLabel: "image file",
    getMedia: getImageFile,
    missing,
  });
}

function collectImageOnlyMissing(themeData, missing) {
  const rows = ensureThemeJsonArray(themeData?.image_only).filter(imageRowHasContent);
  collectMediaStageMissing({
    rows,
    stageLabel: "Stage 5 (Image Only)",
    minQuestions: IMAGE_ONLY_MIN_QUESTIONS,
    isComplete: isImageRowComplete,
    mediaLabel: "image file",
    getMedia: getImageFile,
    missing,
  });
}

/**
 * Validate that themeData has complete content for all 5 game stages.
 * Every listed question must be complete — extra incomplete rows also block the game.
 * @returns {{ ok: boolean, missing: string[] }}
 */
export function validateThemeStageData(themeData) {
  if (!themeData || typeof themeData !== "object") {
    return {
      ok: false,
      missing: ["Theme data is missing. Please configure the theme before starting the game."],
    };
  }

  const missing = [];
  collectWordPuzzleMissing(themeData, missing);
  collectUnscrambleMissing(themeData, missing);
  collectQuizMissing(themeData, missing);
  collectImageQuizMissing(themeData, missing);
  collectImageOnlyMissing(themeData, missing);

  return { ok: missing.length === 0, missing };
}

/** Shared Swal for incomplete theme stage data. */
export function fireThemeStageDataMissingSwal(missing = []) {
  const missingList = missing.map((item) => `<li>${item}</li>`).join("");
  return Swal.fire({
    icon: "warning",
    title: "These are missing",
    html: `<p>Please fill all stage data before starting the game.</p><ul style="text-align:left;margin:12px 0 0;padding-left:18px;max-height:240px;overflow:auto;">${missingList}</ul>`,
    confirmButtonText: "OK",
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
}
